import styled from "styled-components"

type StyledProps = {
  $initialExpanded: boolean
}

export const Container = styled.div`
  border: 0.1rem solid ${(p) => p.theme.colors.borderLight};
  border-radius: 0.6rem;
  background: ${(p) => p.theme.colors.greyLighter};
  margin-bottom: 2rem;
`

export const ChartHeader = styled.div`
  padding: 2rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  font-size: 1.5rem;
  background: ${(p) => p.theme.colors.greyLighter};
  border-radius: 0.7rem;
  span {
    display: flex;
    svg {
      height: 2.5rem;
      margin-right: 2rem;
    }
  }

  svg {
    height: 1.4rem;
  }
`

export const ChartContent = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.$expanded ? "1fr" : "0fr")};
  transition: grid-template-rows 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
`

export const ChartContentInner = styled.div<{ $expanded: boolean }>`
  min-height: 0;
  overflow: hidden;
  opacity: ${(props) => (props.$expanded ? "1" : "0")};
  transition: opacity 0.3s ease ${(props) => (props.$expanded ? "0.1s" : "0s")};
  padding: ${(props) => (props.$expanded ? "1.5rem" : "0")};
  position: relative;
`

export const Header = styled.div<{ $monthCount: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$monthCount}, 1fr);
  gap: 0.1rem;
  margin-bottom: 0.8rem;
  border-radius: 0.4rem;
  overflow: hidden;
`

export const HeaderCell = styled.div`
  padding: 0.8rem;
  background: ${(p) => p.theme.colors.background};
  border-right: 0.2rem solid ${(p) => p.theme.colors.white};
  font-weight: 600;
  text-align: center;
  font-size: 1.3rem;
`

export const ChartWrapper = styled.div<StyledProps>`
  border: 0.1rem solid ${(p) => p.theme.colors.borderLight};
  padding: 1rem 1rem 4rem 1rem;
  background: ${(p) => p.theme.colors.white};
  border-radius: 0.6rem;
  overflow: hidden;
  max-height: ${(p) => (p.$initialExpanded ? "200rem" : "35rem")};
  transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`

export const ChartRow = styled.div`
  position: relative;
  height: 3rem;
  margin-bottom: 0;
  background: ${(p) => p.theme.background};
  border-radius: 0.4rem;
`

export const ShowMore = styled.div`
  background-color: rgba(240, 241, 245);
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  z-index: 2;
  bottom: 0.5rem;
  right: 0rem;
  padding: 0.5rem 0;
  font-weight: bold;
  font-size: 1.3rem;
  transition: all 0.2s ease;

  &:hover {
    cursor: pointer;
    background-color: ${(p) => p.theme.colors.turquoiseDark};
    transform: translateY(-0.1rem);
    color: white;
  }
`

export const TimelineTrack = styled.div<{ $monthCount: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-columns: repeat(${(props) => props.$monthCount}, 1fr);
  pointer-events: none;
  z-index: 1;
`

export const MonthCell = styled.div`
  border-right: 0.2rem solid ${(p) => p.theme.colors.white};
  &:last-child {
    border-right: none;
  }
`

export const ExpandIcon = styled.div<{ $expanded: boolean }>`
  transform: ${(props) => (props.$expanded ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.3s ease;
`

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.2rem;
`

export const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;

  /* Override CheckboxSimple padding for compact layout */
  > div {
    padding: 0.3rem 0;
  }
`

export const ColorInfo = styled.div<{ $color: string; $clickable?: boolean }>`
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  user-select: none;

  &:before {
    content: "";
    flex-shrink: 0;
    background: ${(p) => p.$color};
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
  }

  ${(p) =>
    p.$clickable &&
    `
    &:hover {
      opacity: 0.8;
    }
  `}
`

export const ButtonWrapper = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: flex-end;
  align-items: flex-end;
`

export const MonthButton = styled.button<{ $active: boolean }>`
  padding: 0.8rem 1.6rem;
  border: 0.1rem solid #dee2e6;
  border-radius: 0.4rem;
  background: ${(props) => (props.$active ? props.theme.colors.turquoiseDark : "white")};
  color: ${(props) => (props.$active ? "white" : "#495057")};
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 3rem;

  &:hover {
    background: ${(props) => (props.$active ? props.theme.colors.turquoiseDark : "#f8f9fa")};
  }
`

export const TypeHeader = styled.div`
  padding: 0.8rem 0;
  font-weight: 600;
  font-size: 1.4rem;
  color: ${(p) => p.theme.colors.text};
  margin-top: 1.6rem;
  margin-bottom: 0.8rem;

  &:first-child {
    margin-top: 0;
  }
`

export const SkeletonGroup = styled.div`
  margin-bottom: 1.6rem;
  background: ${(p) => p.theme.colors.greyLightest};
  padding: 1rem;
  border-radius: 0.4rem;
`

export const SkeletonGroupHeader = styled.div`
  width: 12rem;
  height: 1.6rem;
  background: ${(p) => p.theme.colors.borderLight};
  border-radius: 0.4rem;
  margin-bottom: 1rem;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

export const SkeletonBar = styled.div<{ width: string }>`
  height: 3rem;
  width: ${(props) => props.width};
  background: ${(p) => p.theme.colors.borderLight};
  border-radius: 1.5rem;
  margin-bottom: 0.8rem;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`
